import React, { useState } from 'react'
import { ProductImage as ProductImageType } from '@wtf/shared'
import { thumbHashToDataURL } from 'thumbhash'

interface ProductImageProps {
    image: ProductImageType
    alt?: string
    className?: string
    imgClassName?: string
    onClick?: () => void
    loading?: 'lazy' | 'eager'
    sizes?: string
}

/**
 * ProductImage component with progressive loading and WebP variant support
 *
 * Features:
 * - Progressive loading with thumbHash blur placeholder
 * - Structured variants from Asset model for optimal responsive images
 * - Automatic srcSet generation with proper widths
 * - Layout shift prevention with width/height attributes
 * - Browser-optimized picture element with WebP source
 *
 * Props:
 * - className: Applied to the wrapper div for layout and positioning
 * - imgClassName: Applied directly to the img/picture elements for sizing and object-fit
 */
import { replaceImageDomain } from '@/utils/replaceImageDomain'

export const ProductImage: React.FC<ProductImageProps> = ({
    image,
    alt = 'Product',
    className = '',
    imgClassName = '',
    onClick,
    loading = 'lazy',
    sizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
}) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const hasVariants = image.variants && image.variants.length > 0

    // Generate thumbHash placeholder
    const placeholderUrl = image.thumbHash
        ? thumbHashToDataURL(
              Uint8Array.from(atob(image.thumbHash), (c) => c.charCodeAt(0))
          )
        : undefined

    // Replace domain in main image URL
    const imageUrl = replaceImageDomain(image.url)

    // Build srcSet from structured variants with domain replacement
    const srcSet = hasVariants
        ? image.variants
              .map(({ url, width }) => `${replaceImageDomain(url)} ${width}w`)
              .join(', ')
        : undefined

    // Wrapper div for progressive loading effect
    const imageContent = (
        <>
            {/* Blur placeholder */}
            {placeholderUrl && !isLoaded && (
                <img
                    src={placeholderUrl}
                    alt=""
                    className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
                    aria-hidden="true"
                />
            )}

            {/* Main image */}
            {hasVariants && srcSet ? (
                <picture className="relative h-full">
                    <source srcSet={srcSet} sizes={sizes} type="image/webp" />
                    <img
                        src={imageUrl}
                        alt={alt}
                        className={`relative transition-opacity duration-300 ${
                            isLoaded ? 'opacity-100' : 'opacity-0'
                        } ${imgClassName}`}
                        loading={loading}
                        width={image.width || undefined}
                        height={image.height || undefined}
                        onLoad={() => setIsLoaded(true)}
                    />
                </picture>
            ) : (
                <img
                    src={imageUrl}
                    alt={alt}
                    className={`relative transition-opacity duration-300 ${
                        isLoaded ? 'opacity-100' : 'opacity-0'
                    } ${imgClassName}`}
                    loading={loading}
                    width={image.width || undefined}
                    height={image.height || undefined}
                    onLoad={() => setIsLoaded(true)}
                />
            )}
        </>
    )

    // Wrap in clickable div if onClick provided
    return (
        <div
            className={`relative overflow-hidden ${className}`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
        >
            {imageContent}
        </div>
    )
}
